import React, {useEffect, useState} from 'react';
import {Autocomplete, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography} from '@mui/material';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';
import {Button} from 'reactstrap';
import authenticationManager from '../../Auth/AuthenticationManager';
import BroadcastEmailApiService from '../../api/BroadcastEmailApiService';
import UserApiService from '../../api/UserApiService';

export default function EmailSubscribers(props) {
    const {isMobile, setSnackbarProperties} = props;

    const broadcastEmailApiService = new BroadcastEmailApiService();
    const userApiService = new UserApiService();
    const navigate = useNavigate();

    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [subscriberCount, setSubscriberCount] = useState(0);
    const [users, setUsers] = useState([]);
    const [additionalUsers, setAdditionalUsers] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!authenticationManager.isAuthorized || !authenticationManager.isSuperAdmin()) {
            navigate('/');
            return;
        }

        document.title = 'Executive Storage Suites - Email Subscribers';

        void getSubscriberCount();
        void getUsers();
    }, []);

    async function getSubscriberCount() {
        const response = await broadcastEmailApiService.getSubscriberCount();

        if (response?.success) {
            setSubscriberCount(response.data);
        } else {
            setSnackbarProperties(`Error getting subscriber count: ${response?.message}`, 'error');
        }
    }

    async function getUsers() {
        const response = await userApiService.getAllUsersGeneric();

        if (response?.success) {
            setUsers(response.data);
        } else {
            setSnackbarProperties(`Error getting users: ${response?.message}`, 'error');
        }
    }

    async function sendEmail() {
        setSending(true);
        const additionalEmails = additionalUsers.map((user) => user.username);
        const response = await broadcastEmailApiService.sendToSubscribers(subject, body, additionalEmails);

        if (response?.success) {
            setSnackbarProperties(`Email queued for ${response.data} recipient${response.data === 1 ? '' : 's'}`, 'success');
            setSubject('');
            setBody('');
            setAdditionalUsers([]);
        } else {
            setSnackbarProperties(`Error sending email: ${response?.message}`, 'error');
        }

        setSending(false);
        setConfirmOpen(false);
    }

    return (
        <Box>
            <h1>Email Subscribers</h1>
            <Typography sx={{marginBottom: '10px'}}>
                This email will be BCC&apos;d to all users with an active subscription ({subscriberCount} subscriber{subscriberCount === 1 ? '' : 's'}), plus any additional users you select below.
            </Typography>
            <Box sx={{maxWidth: isMobile ? '100%' : '600px'}}>
                <Autocomplete
                    multiple
                    options={users}
                    value={additionalUsers}
                    onChange={(event, newValue) => setAdditionalUsers(newValue)}
                    getOptionLabel={(user) => `${user.firstName} ${user.lastName} (${user.username})`}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => <TextField {...params} label={'Additional Recipients'} />}
                    sx={{marginBottom: '10px'}}
                />
                <TextField
                    fullWidth
                    label={'Subject'}
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    sx={{marginBottom: '10px'}}
                />
                <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label={'Message'}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    sx={{marginBottom: '10px'}}
                />
                <Button
                    className={isMobile ? 'mobile-modal-button' : ''}
                    variant={'contained'}
                    disabled={!subject.trim() || !body.trim() || (subscriberCount === 0 && additionalUsers.length === 0)}
                    onClick={() => setConfirmOpen(true)}
                >
                    Send Email
                </Button>
            </Box>
            <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)}>
                <DialogTitle>Send to all subscribers?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`"${subject}" will be BCC'd to ${subscriberCount} subscriber${subscriberCount === 1 ? '' : 's'}${additionalUsers.length > 0 ? ` and ${additionalUsers.length} additional recipient${additionalUsers.length === 1 ? '' : 's'}` : ''}. This cannot be undone.`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button disabled={sending} onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button disabled={sending} onClick={sendEmail}>{sending ? 'Sending...' : 'Send'}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

EmailSubscribers.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};